import React, { useRef, useState } from 'react';
import {
  downloadProductsImportTemplate,
  analyzeProductsImport,
  executeProductsImport,
  summarizeImportResult,
} from '../../utils/productsExcelImport';
import ProductsImportPreviewModal from './ProductsImportPreviewModal';

const btnClass =
  'px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

const ProductsExcelToolbar = ({ onImported, setToast, insumoEnabled = false, machineryEnabled = false }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewState, setPreviewState] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const closePreview = () => {
    if (confirming) return;
    setPreviewOpen(false);
    setPreviewState(null);
  };

  const download = async () => {
    try {
      setBusy(true);
      await downloadProductsImportTemplate({ insumoEnabled, machineryEnabled });
      setToast({
        isOpen: true,
        message: 'Plantilla de productos descargada. Incluye columnas Sí/No para módulos extra cuando estén activos.',
        type: 'success',
      });
    } catch {
      setToast({ isOpen: true, message: 'No se pudo generar la plantilla Excel.', type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setBusy(true);
    try {
      const analysis = await analyzeProductsImport(file, { insumoEnabled, machineryEnabled });
      if (!analysis.success) {
        setToast({
          isOpen: true,
          message: analysis.fatalMessage || 'No se pudo leer el archivo.',
          type: 'error',
        });
        return;
      }
      setPreviewState(analysis);
      setPreviewOpen(true);
    } catch (err) {
      setToast({
        isOpen: true,
        message: err?.message || 'No se pudo leer el archivo. Use una plantilla .xlsx.',
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!previewState?.success) return;

    setConfirming(true);
    try {
      const exec = await executeProductsImport(previewState.validTasks);
      const merged = { ...exec, skipped: previewState.emptyCount };
      const message = summarizeImportResult(merged);
      const hasErr = merged.errors.length > 0;
      const type =
        !merged.created && hasErr ? 'error' : hasErr ? 'warning' : 'success';

      setToast({ isOpen: true, message, type });
      setPreviewOpen(false);
      setPreviewState(null);
      if (merged.created > 0) onImported?.();
    } catch (err) {
      setToast({
        isOpen: true,
        message: err?.message || 'Error al importar.',
        type: 'error',
      });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={btnClass} onClick={download} disabled={busy || confirming}>
          Plantilla importar productos
        </button>
        <button
          type="button"
          className={btnClass}
          onClick={() => inputRef.current?.click()}
          disabled={busy || confirming}
        >
          {busy ? 'Leyendo…' : 'Importar productos (Excel)'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={onFileChange}
        />
      </div>

      <ProductsImportPreviewModal
        isOpen={previewOpen}
        onClose={closePreview}
        preview={previewState}
        onConfirm={handleConfirmImport}
        confirming={confirming}
      />
    </>
  );
};

export default ProductsExcelToolbar;
